import { useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import type { ForecastStabilityData, ForecastStabilityGrain } from '../../../lib/api/po-line-dashboard';
import { useFormatters } from '../../../contexts/PreferencesContext';

// Same palette as the Scheduled Dashboard's Demand Movement chart, so the two
// stability views read as one family.
const ADDED_COLOR = '#0f766e';
const REMOVED_COLOR = '#dc2626';

const SERIES_LABELS: Record<string, string> = {
  added: 'Added since baseline',
  removed: 'Removed since baseline',
};

function bucketLabel(iso: string, grain: ForecastStabilityGrain): string {
  const d = new Date(iso + 'T00:00:00Z');
  return grain === 'month'
    ? d.toLocaleDateString(undefined, { month: 'short', year: '2-digit', timeZone: 'UTC' })
    : d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', timeZone: 'UTC' });
}

function bucketPhrase(label: string, grain: ForecastStabilityGrain): string {
  return grain === 'month' ? label : `Week of ${label}`;
}

interface ChartRow {
  bucket: string;
  added: number;
  /** Negative, so it stacks below zero. */
  removed: number;
  /** Total currently forecast — shown in the tooltip. */
  current: number;
}

interface ForecastMovementChartProps {
  data: ForecastStabilityData | null;
  grain: ForecastStabilityGrain;
}

/**
 * Forward forecast movement, one diverging bar per delivery bucket (week or
 * month) from today onwards: qty added since the baseline copy above zero
 * (green), qty removed below (red). The bucket's total forecast qty shows in
 * the tooltip. Deltas are computed per part per bucket server-side, so
 * opposite moves on different parts don't cancel.
 */
export function ForecastMovementChart({ data, grain }: ForecastMovementChartProps) {
  // CR-7262: these totals are sums of forecast quantities and appear in prose,
  // not on an axis, so they carry the same decimals as the quantities behind
  // them. Was a module-level `wholeUnits`, which rounded them away.
  const { formatQuantity } = useFormatters();
  const rows: ChartRow[] = useMemo(
    () =>
      (data?.forward?.buckets ?? []).map((b) => ({
        bucket: bucketLabel(b.bucket, grain),
        added: b.added,
        removed: -b.removed,
        current: b.current,
      })),
    [data, grain],
  );
  const empty = rows.every((r) => r.added === 0 && r.removed === 0 && r.current === 0);
  // No copy to compare against yet: movement is UNKNOWN, never 'unchanged'.
  const noBaseline = !empty && data?.source === 'none';
  // Group view: sites with no copy contribute current only, so "unchanged" must
  // be scoped to the sites that HAVE a copy.
  const baselines = Object.values(data?.forward?.baselines ?? {});
  const sitesWithoutCopy = baselines.filter((d) => d === null).length;
  // Forecast in scope but nothing moved: say so, or a bar-less axis reads as broken.
  const unchanged = !empty && !noBaseline && rows.every((r) => r.added === 0 && r.removed === 0);
  const totalCurrent = rows.reduce((sum, r) => sum + r.current, 0);
  const baseline = data?.forward?.baseline ? bucketLabel(data.forward.baseline, 'week') : null;
  const unit = grain === 'month' ? 'month' : 'week';

  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-slate-700">
          Forecast Movement — This {grain === 'month' ? 'Month' : 'Week'} Onwards
        </CardTitle>
        <p className="text-[11px] text-muted-foreground">
          Qty added to (▲) and removed from (▼) each forward delivery {unit}
          {baseline ? ` since the copy taken ${baseline}` : ''}. Hover a {unit} for its total forecast qty.
        </p>
      </CardHeader>
      <CardContent>
        {empty ? (
          <div className="h-96 flex items-center justify-center text-sm text-muted-foreground">
            No forward forecast in scope — adjust the supplier / part filters.
          </div>
        ) : noBaseline ? (
          <div className="h-96 flex flex-col items-center justify-center gap-1 text-sm text-muted-foreground">
            <span>No baseline copy yet — movement is unknown until a copy taken before the current forecast import exists.</span>
            <span className="text-xs">{formatQuantity(totalCurrent)} forecast across the horizon today.</span>
          </div>
        ) : unchanged ? (
          <div className="h-96 flex flex-col items-center justify-center gap-1 text-sm text-muted-foreground">
            <span>
              {sitesWithoutCopy > 0
                ? `No movement on the sites with a copy${baseline ? ` since ${baseline}` : ''} — ${sitesWithoutCopy} of ${baselines.length} sites have no copy yet, so their movement is unknown.`
                : `No movement${baseline ? ` since the copy taken ${baseline}` : ''} — the forecast in scope is unchanged.`}
            </span>
            <span className="text-xs">{formatQuantity(totalCurrent)} forecast across the horizon.</span>
          </div>
        ) : (
          <div className="h-96">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={rows} margin={{ top: 5, right: 10, bottom: 0, left: -10 }} stackOffset="sign">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis
                  dataKey="bucket"
                  tick={{ fontSize: 10, fill: '#64748b' }}
                  axisLine={{ stroke: '#cbd5e1' }}
                  tickLine={false}
                  interval={grain === 'month' ? 0 : 1}
                />
                <YAxis tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                <Tooltip
                  formatter={(value: number, name: string) => [
                    Math.abs(value).toLocaleString(),
                    SERIES_LABELS[name] ?? name,
                  ]}
                  labelFormatter={(label: string, items: ReadonlyArray<{ payload?: ChartRow }>) => {
                    const current = items?.[0]?.payload?.current;
                    const phrase = bucketPhrase(label, grain);
                    return current != null
                      ? `${phrase} — ${current.toLocaleString()} forecast`
                      : phrase;
                  }}
                />
                <Legend
                  formatter={(v: string) => SERIES_LABELS[v] ?? v}
                  wrapperStyle={{ fontSize: 11 }}
                />
                <ReferenceLine y={0} stroke="#94a3b8" />
                <Bar dataKey="added" stackId="mv" fill={ADDED_COLOR} isAnimationActive={false} maxBarSize={28} />
                <Bar dataKey="removed" stackId="mv" fill={REMOVED_COLOR} isAnimationActive={false} maxBarSize={28} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
